import React, { useMemo, useState } from "react";
import {
  ShoppingCart,
  Sparkles,
  Clock3,
  Truck,
  CheckCircle2,
  XCircle,
  IndianRupee,
  Package,
  UserX,
} from "lucide-react";
import { isInFilter } from "../utils/dateFilter";

const FILTER_TABS = ["Today", "Yesterday", "Weekly", "Monthly", "All Time", "Custom Date"];

const PIPELINE_STATUSES = new Set([
  "Pending",
  "Processing",
  "Packed",
  "Shipped",
  "Out for delivery",
]);

function getFilterLabel(filter) {
  if (typeof filter === "string") return filter;
  if (filter?.type === "Custom Date" && filter.from && filter.to) {
    return `${filter.from} to ${filter.to}`;
  }
  return "Selected period";
}

function formatCurrency(value) {
  const amount = Number(value) || 0;
  return `₹${amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function isArchived(order) {
  return order.archive === true || order.archive === "true";
}

const SummaryDateFilter = ({ value, onChange }) => {
  const [activeTab, setActiveTab] = useState(
    typeof value === "string" ? value : value?.type === "Custom Date" ? "Custom Date" : "Today"
  );
  const [showCustom, setShowCustom] = useState(activeTab === "Custom Date");
  const [tempRange, setTempRange] = useState({
    from: value?.from || "",
    to: value?.to || "",
  });

  const handleTabClick = (tab) => {
    if (tab === "Custom Date") {
      setActiveTab("Custom Date");
      setShowCustom(true);
      return;
    }
    setActiveTab(tab);
    setShowCustom(false);
    onChange(tab);
  };

  const handleApplyCustom = () => {
    if (!tempRange.from || !tempRange.to || tempRange.from > tempRange.to) return;
    onChange({ type: "Custom Date", from: tempRange.from, to: tempRange.to });
    setShowCustom(false);
  };

  return (
    <div className="w-full sm:w-auto sm:min-w-[280px]">
      <div className="flex items-center gap-1 dash-filter-scroll rounded-xl border border-slate-200 bg-white p-1">
        {FILTER_TABS.map((tab) => {
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => handleTabClick(tab)}
              className={`shrink-0 rounded-lg px-2.5 py-1.5 text-[11px] sm:text-xs font-semibold transition-colors whitespace-nowrap ${
                isActive
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
              }`}
            >
              {tab}
            </button>
          );
        })}
      </div>

      {showCustom && (
        <div className="mt-2 rounded-xl border border-dashed border-slate-300 bg-white p-2.5">
          <div className="flex flex-col sm:flex-row gap-2">
            <label className="flex flex-col gap-1 text-[11px] font-semibold text-slate-600 flex-1">
              From
              <input
                type="date"
                value={tempRange.from}
                onChange={(e) => setTempRange((prev) => ({ ...prev, from: e.target.value }))}
                className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs outline-none focus:border-emerald-400"
              />
            </label>
            <label className="flex flex-col gap-1 text-[11px] font-semibold text-slate-600 flex-1">
              To
              <input
                type="date"
                value={tempRange.to}
                onChange={(e) => setTempRange((prev) => ({ ...prev, to: e.target.value }))}
                className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs outline-none focus:border-emerald-400"
              />
            </label>
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowCustom(false);
                if (typeof value === "string") setActiveTab(value);
              }}
              className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApplyCustom}
              disabled={!tempRange.from || !tempRange.to || tempRange.from > tempRange.to}
              className="rounded-lg bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ label, value, hint, tone = "default", icon: Icon, active, onClick }) => {
  const toneClass =
    tone === "success"
      ? "text-emerald-700"
      : tone === "warn"
        ? "text-amber-700"
        : tone === "danger"
          ? "text-red-700"
          : "text-slate-800";

  const Tag = onClick ? "button" : "div";

  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`rounded-lg border px-3 py-2 min-w-[118px] text-left transition-all ${
        active
          ? "border-emerald-500 bg-white ring-2 ring-emerald-200 shadow-sm"
          : "border-white/70 bg-white/80 hover:border-emerald-200 hover:shadow-sm"
      } ${onClick ? "cursor-pointer" : ""}`}
      aria-pressed={onClick ? active : undefined}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</p>
        {Icon ? <Icon size={14} className="text-slate-400 shrink-0" /> : null}
      </div>
      <p className={`text-lg font-bold ${toneClass}`}>{value}</p>
      {hint ? <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p> : null}
    </Tag>
  );
};

const STATUS_BAR = [
  { key: "Pending", color: "#f59e0b", tab: "pending" },
  { key: "Processing", color: "#3b82f6", tab: "processing" },
  { key: "Packed", color: "#10b981", tab: "packed" },
  { key: "Shipped", color: "#06b6d4", tab: "shipped" },
  { key: "Out for delivery", color: "#a855f7", tab: "out for delivery" },
  { key: "Delivered", color: "#22c55e", tab: "delivered" },
  { key: "Cancelled", color: "#ef4444", tab: "cancelled" },
];

export default function OrderPageSummary({ orders = [], activeTab, onTabChange }) {
  const [filter, setFilter] = useState("All Time");

  const summary = useMemo(() => {
    const periodOrders = (Array.isArray(orders) ? orders : []).filter(
      (order) => !isArchived(order) && isInFilter(order.createdAt || order.updatedAt, filter)
    );

    const countByStatus = (status) =>
      periodOrders.filter((order) => order.orderStatus === status).length;

    const deliveredOrders = periodOrders.filter((order) => order.orderStatus === "Delivered");
    const revenue = deliveredOrders.reduce(
      (sum, order) => sum + (Number(order.amountafterdiscount) || 0),
      0
    );
    const pipelineOrders = periodOrders.filter(
      (order) => PIPELINE_STATUSES.has(order.orderStatus)
    );
    const pipelineValue = pipelineOrders.reduce(
      (sum, order) => sum + (Number(order.amountafterdiscount) || 0),
      0
    );
    const unassigned = periodOrders.filter(
      (order) => !order.staff_id && !order.staff_name
    ).length;
    const paymentPending = periodOrders.filter(
      (order) => String(order.status || "").toLowerCase() === "pending"
    ).length;

    const statusCounts = STATUS_BAR.map((item) => ({
      ...item,
      count: countByStatus(item.key),
    }));
    const statusTotal = statusCounts.reduce((sum, item) => sum + item.count, 0) || 1;

    return {
      filterLabel: getFilterLabel(filter),
      total: periodOrders.length,
      newCount: periodOrders.filter((order) => order.mark === "New").length,
      pending: countByStatus("Pending"),
      processing: countByStatus("Processing"),
      packed: countByStatus("Packed"),
      shipped: countByStatus("Shipped"),
      outForDelivery: countByStatus("Out for delivery"),
      delivered: deliveredOrders.length,
      cancelled: countByStatus("Cancelled"),
      pipeline: pipelineOrders.length,
      revenue,
      pipelineValue,
      avgOrderValue: deliveredOrders.length ? Math.round(revenue / deliveredOrders.length) : 0,
      unassigned,
      paymentPending,
      statusCounts,
      statusTotal,
    };
  }, [orders, filter]);

  const switchTab = (tab) => {
    if (onTabChange) onTabChange(tab);
  };

  return (
    <section className="order-page-summary mb-4">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-base sm:text-lg font-bold text-slate-900">Order Summary</h2>
          <p className="text-xs sm:text-sm text-slate-500">
            Fulfillment pulse, revenue, and pipeline health — {summary.filterLabel}
          </p>
        </div>
        <SummaryDateFilter value={filter} onChange={setFilter} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 mb-3">
        <div className="rounded-xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-teal-50 p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-white/80 flex items-center justify-center text-emerald-700 shadow-sm">
              <ShoppingCart size={16} />
            </div>
            <h3 className="text-sm sm:text-base font-bold text-slate-800">Volume</h3>
          </div>
          <div className="flex gap-2 dash-metric-scroll dash-metric-scroll--orders pb-1">
            <MetricCard
              label="Total Orders"
              value={summary.total}
              hint="In selected period"
              icon={ShoppingCart}
              active={activeTab === "all"}
              onClick={() => switchTab("all")}
            />
            <MetricCard
              label="New"
              value={summary.newCount}
              hint="Unread orders"
              icon={Sparkles}
              tone={summary.newCount > 0 ? "warn" : "default"}
              active={activeTab === "new"}
              onClick={() => switchTab("new")}
            />
            <MetricCard
              label="In Pipeline"
              value={summary.pipeline}
              hint="Pending to delivery"
              icon={Truck}
              tone="warn"
            />
            <MetricCard
              label="Unassigned"
              value={summary.unassigned}
              hint="No staff assigned"
              icon={UserX}
              tone={summary.unassigned > 0 ? "warn" : "default"}
            />
            <MetricCard
              label="Payment Pending"
              value={summary.paymentPending}
              hint="Awaiting payment"
              icon={Clock3}
              tone={summary.paymentPending > 0 ? "danger" : "default"}
            />
          </div>
        </div>

        <div className="rounded-xl border border-teal-100 bg-gradient-to-br from-teal-50 to-cyan-50 p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-white/80 flex items-center justify-center text-teal-700 shadow-sm">
              <IndianRupee size={16} />
            </div>
            <h3 className="text-sm sm:text-base font-bold text-slate-800">Revenue</h3>
          </div>
          <div className="flex gap-2 dash-metric-scroll dash-metric-scroll--orders pb-1">
            <MetricCard
              label="Delivered Revenue"
              value={formatCurrency(summary.revenue)}
              hint="Delivered order value"
              icon={IndianRupee}
              tone="success"
              active={activeTab === "delivered"}
              onClick={() => switchTab("delivered")}
            />
            <MetricCard
              label="Pipeline Value"
              value={formatCurrency(summary.pipelineValue)}
              hint="Open order value"
              icon={Package}
            />
            <MetricCard
              label="Avg Order Value"
              value={formatCurrency(summary.avgOrderValue)}
              hint="Per delivered order"
            />
            <MetricCard
              label="Delivered"
              value={summary.delivered}
              icon={CheckCircle2}
              tone="success"
              active={activeTab === "delivered"}
              onClick={() => switchTab("delivered")}
            />
            <MetricCard
              label="Cancelled"
              value={summary.cancelled}
              icon={XCircle}
              tone={summary.cancelled > 0 ? "danger" : "default"}
              active={activeTab === "cancelled"}
              onClick={() => switchTab("cancelled")}
            />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4 mb-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
          Status breakdown
        </p>
        <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
          {summary.statusCounts.map((item) =>
            item.count > 0 ? (
              <button
                key={item.key}
                type="button"
                title={`${item.key}: ${item.count}`}
                onClick={() => switchTab(item.tab)}
                className="h-full transition-opacity hover:opacity-80"
                style={{
                  width: `${(item.count / summary.statusTotal) * 100}%`,
                  backgroundColor: item.color,
                  minWidth: item.count > 0 ? "4px" : 0,
                }}
                aria-label={`${item.key} ${item.count}`}
              />
            ) : null
          )}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-600">
          {summary.statusCounts.map((item) => (
            <button
              key={`${item.key}-legend`}
              type="button"
              onClick={() => switchTab(item.tab)}
              className={`inline-flex items-center gap-1 hover:text-slate-900 ${
                activeTab === item.tab ? "font-bold text-slate-900" : ""
              }`}
            >
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              {item.key} ({item.count})
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-slate-600">
        <div className="flex items-start gap-2 rounded-lg bg-white border border-emerald-100 px-3 py-2">
          <Sparkles size={14} className="text-amber-500 mt-0.5 shrink-0" />
          <span>
            {summary.newCount > 0
              ? `${summary.newCount} new order(s) need a first review. Open the New tab to process them.`
              : "No unread new orders in this period."}
          </span>
        </div>
        <div className="flex items-start gap-2 rounded-lg bg-white border border-teal-100 px-3 py-2">
          <Truck size={14} className="text-teal-600 mt-0.5 shrink-0" />
          <span>
            {summary.pipeline > 0
              ? `${summary.pipeline} order(s) are still in the fulfillment pipeline worth ${formatCurrency(summary.pipelineValue)}.`
              : "No open pipeline orders in this period."}
          </span>
        </div>
        <div className="flex items-start gap-2 rounded-lg bg-white border border-slate-200 px-3 py-2">
          <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" />
          <span>
            {summary.delivered > 0
              ? `${summary.delivered} delivered order(s) generated ${formatCurrency(summary.revenue)} in this period.`
              : "No delivered orders in this period yet."}
          </span>
        </div>
      </div>
    </section>
  );
}
